import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Divider, Button, Typography, Alert } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '@/component/Header';
import StakeDbPoolForm from '@/component/stake-db/pool/StakeDbPoolForm';
import StakeDbPoolStateSelect from '@/component/stake-db/pool/StakeDbPoolStateSelect';
import { updateStakeDbPool, deleteStakeDbPool, readStakeDbPoolDetail } from '@/api/stake-db';
import { CreateStakeDbPoolRequest, UpdateStakeDbPoolRequest, StakeDbPool, STAKE_DB_POOL_STATE } from '@/types/stake-db';
import { stakeDbStateFilter } from '@/lib/filter/stakeDbPoolState';

export default function StakeDbPoolDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [poolData, setPoolData] = useState<StakeDbPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchPoolDetail = async () => {
      const poolId = location.state?.rowValue?.poolId;
      if (!poolId) {
        toast.error('Pool data not found.');
        navigate('/stake-db-pool-list');
        return;
      }

      setLoading(true);
      try {
        const re = await readStakeDbPoolDetail(poolId);
        if (re.status === 200) {
          // sendRequest가 이미 camelCase로 변환해줌
          setPoolData(re.data);
        } else {
          toast.error('Failed to fetch pool detail.');
          navigate('/stake-db-pool-list');
        }
      } catch (error) {
        console.error('Fetch pool detail error:', error);
        toast.error('Failed to fetch pool detail.');
        navigate('/stake-db-pool-list');
      } finally {
        setLoading(false);
      }
    };

    fetchPoolDetail();
  }, [location.state, navigate]);

  const isOperating =
    poolData?.state === STAKE_DB_POOL_STATE.OPERATING ||
    poolData?.state === STAKE_DB_POOL_STATE.OPERATING_CLOSED ||
    poolData?.state === STAKE_DB_POOL_STATE.OPERATING_STOPPED;

  const handleSubmit = async (data: CreateStakeDbPoolRequest | UpdateStakeDbPoolRequest) => {
    try {
      const re = await updateStakeDbPool(data as UpdateStakeDbPoolRequest);

      if (re.status === 200) {
        toast.success(re.data.message || 'Pool updated successfully.');
        navigate('/stake-db-pool-list');
      } else {
        toast.error(re.data?.message || 'Failed to update pool.');
      }
    } catch (error) {
      console.error('Update pool error:', error);
      toast.error('Failed to update pool.');
    }
  };

  const handleStateChanged = () => {
    navigate('/stake-db-pool-list');
  };

  const handleDelete = async () => {
    if (!poolData?.poolId) return;

    try {
      const re = await deleteStakeDbPool(poolData.poolId);

      if (re.status === 200) {
        toast.success(re.data.message || 'Pool deleted successfully.');
        navigate('/stake-db-pool-list');
      } else {
        toast.error(re.data?.message || 'Failed to delete pool.');
      }
    } catch (error) {
      console.error('Delete pool error:', error);
      toast.error('Failed to delete pool.');
    }
  };

  if (loading || !poolData) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: 'calc(100vh - 64px)',
        overflowY: 'auto',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
      {/* Pool Info Card */}
      <Card>
        <Header title={`Pool Detail: ${poolData.poolId}`} />
        <Divider />
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Current State: <strong>{stakeDbStateFilter(poolData.state)}</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Pool Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Edit Pool Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <StakeDbPoolForm
            key={poolData.poolId}
            mode="edit"
            initialData={poolData}
            onSubmit={handleSubmit}
            isOperating={isOperating}
          />
        </CardContent>
      </Card>

      {/* State Change Card */}
      <Card>
        <CardContent>
          <StakeDbPoolStateSelect
            poolId={poolData.poolId}
            currentState={poolData.state}
            onStateChanged={handleStateChanged}
          />
        </CardContent>
      </Card>

      {/* Delete Pool Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            Danger Zone
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="warning" sx={{ mb: 2 }}>
            Deleting a pool is irreversible. Pools with staking history cannot be deleted.
          </Alert>

          {!deleteConfirm ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete Pool
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Confirm Delete
              </Button>
              <Button
                variant="outlined"
                onClick={() => setDeleteConfirm(false)}
              >
                Cancel
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
}
